import { defineStore } from "pinia";
import { Emoji, InteractivityEmoji, Reply, ReplyOwner } from "@/types/entity";
import { useMate } from "@/stores/mate";
import api from "@/modules/api";
import { entityStoreFactory } from "@/stores/factory/entity-store-factory";
import { computed, ref } from "vue";
import { StorageGet, StorageSet } from "@/tools/localStorage";
import { useGlobalUser } from "@/stores/globalUser";

const PER_PAGE = 200;

/**
 * 为什么Reply需要单独一个store？
 * 1. 为了全局统一显示Reply的Count和Reaction
 * 2. 减少重复请求
 * 3. 为了在Moment和Story等页面中，复用Reply的逻辑
 */
export const useReply = defineStore("reply", () => {
  const showEmojiPopup = ref(false);
  const emojiReplyTarget = ref<Reply | null>(null);
  // entity
  const replyEntity = entityStoreFactory<Reply>("id");
  const { updateMap, getEntity, deleteItem } = replyEntity;
  // rootId2ReplyId
  const rootId2ReplyId = ref<Record<number, number[]>>([]);
  const replyOwnerMap = ref<Record<number, ReplyOwner | undefined>>({});
  const countOwnerMap = ref<
    Record<number, { emojiCount: number; replyCount: number }>
  >({});
  const entityId2ReplyId = ref<Record<number, number[]>>([]);
  // emoji
  const emojiList = ref<Emoji[]>([]);
  const emojiMap = computed(() => {
    const map = new Map<number, Emoji>();
    emojiList.value?.forEach((item) => {
      map.set(item.id, item);
    });
    return map;
  });

  function getReply(replyId: number): Reply | undefined {
    return getEntity(replyId);
  }

  function getReplyCount(id: number): number {
    return rootId2ReplyId.value[id]?.length ?? 0;
  }

  async function getOrFetchReplyList(
    rootId: number,
    force = false
  ): Promise<Reply[]> {
    if (!force && rootId2ReplyId.value[rootId]) {
      return rootId2ReplyId.value[rootId]!.map((id) => getEntity(id)!);
    } else {
      return fetchReplyList(rootId);
    }
  }

  function getReplyList(rootId: number): Reply[] | null {
    if (!rootId2ReplyId.value[rootId]) {
      return null;
    }
    return rootId2ReplyId.value[rootId]!.map((id) => getEntity(id)!);
  }

  async function fetchReplyList(rootId: number) {
    const res = await api.readReplyPage({
      filter: { rootId },
      sort: { field: "dtCreate", order: "desc" },
      pagination: { page: 1, perPage: PER_PAGE },
    });
    const replyList = updateMap(res.page.data);
    rootId2ReplyId.value[rootId] = res.page.data.map((item) => item.id!);

    if (res.page.data.length > 0) {
      const reply = getReply(rootId);
      if (reply) {
        const newCount = res.page.data.filter(
          (item) => item.parentId === rootId
        ).length;
        reply.replyCount = Math.max(reply.replyCount, newCount);
      }
    }
    return replyList;
  }

  function getReplyTreeList(entityId: number): Reply[] | null {
    if (!entityId2ReplyId.value[entityId]) return null;
    return entityId2ReplyId.value[entityId]!.map((id) => getEntity(id)!);
  }

  async function getOrFetchReplyTreeList(
    entityId: number,
    entityType: "App\\Models\\StoryBook" | "App\\Models\\Collection",
    force = false
  ): Promise<Reply[]> {
    if (!force && entityId2ReplyId.value[entityId]) {
      return entityId2ReplyId.value[entityId]!.map((id) => getEntity(id)!);
    } else {
      return fetchReplyTreeList(entityId, entityType);
    }
  }

  async function fetchReplyTreeList(
    entityId: number,
    entityType: "App\\Models\\StoryBook" | "App\\Models\\Collection"
  ) {
    const res = await api.readReplyTreePage({
      filter: {
        entityId,
        entityType,
      },
      sort: { field: "dtCreate", order: "desc" },
      pagination: { page: 1, perPage: PER_PAGE },
    });
    const { data } = res.page;
    data.sort((a, b) =>
      Number(new Date(a.dtCreate!)) - Number(new Date(b.dtCreate!)) > 0 ? -1 : 1
    );
    const replyList = updateMap(data);
    const idMap: number[] = [];
    countOwnerMap.value[entityId] = { emojiCount: 0, replyCount: 0 };
    data.forEach((item) => {
      if (item.owner) {
        replyOwnerMap.value[item.id!] = item.owner;
      } else idMap.push(item.id!);
      countOwnerMap.value[entityId].emojiCount += item.emojiCount;
      countOwnerMap.value[entityId].replyCount += item.replyCount;
    });
    entityId2ReplyId.value[entityId] = idMap;
    return replyList;
  }

  async function initEmojiVoteList() {
    const cache = StorageGet("emojiList");
    if (cache) {
      emojiList.value = cache;
    } else {
      const res = await api.readEmojiPage();
      StorageSet("emojiList", res.page.data);
      emojiList.value = res.page.data;
    }
  }

  const voteLoading = ref(false);

  async function vote(emojiId: number, replyId: number) {
    voteLoading.value = true;
    try {
      const targetReply = getEntity(replyId);
      if (!targetReply) return;
      // 1. 更新/初始化首页徽标
      const mate = useMate();
      const res = await api.createEmojiVote({
        emojiId,
        replyId: targetReply.id!,
        mateId: mate.currentMate!.uuid,
      });
      let targetEmoji: InteractivityEmoji | undefined =
        targetReply.interactivityEmoji.find((item) => item.id === emojiId);
      if (res.item) {
        // 新vote
        if (targetEmoji) {
          targetEmoji.amount++;
        } else {
          targetEmoji = {
            id: emojiId,
            content: emojiMap.value.get(emojiId)!.content,
            amount: 1,
          };
          targetReply.interactivityEmoji.push(targetEmoji);
        }
        targetReply.emojiVotezz?.push({
          ...res.item,
          emoji: emojiMap.value.get(emojiId)!,
        });
        targetReply.emojiCount++;
      } else {
        // 取消vote
        if (targetEmoji) {
          targetEmoji.amount--;
          if (targetEmoji.amount === 0) {
            targetReply.interactivityEmoji =
              targetReply.interactivityEmoji.filter(
                (item) => item.id !== emojiId
              );
          }
        }
        targetReply.emojiVotezz = targetReply.emojiVotezz?.filter(
          (item) =>
            item.emojiId !== emojiId || item.mateId !== mate.currentMate?.id
        );
        targetReply.emojiCount = Math.max(targetReply.emojiCount - 1, 0);
      }
    } finally {
      voteLoading.value = false;
    }
  }

  async function deleteReply(replyId: number) {
    // 1. 请求删除
    await api.deleteReply(replyId);
    // 2. 更新父层
    const targetReply = getEntity(replyId);
    if (targetReply?.rootId) {
      rootId2ReplyId.value[targetReply.rootId] = rootId2ReplyId.value[
        targetReply.rootId
      ]!.filter((id) => id !== replyId);
    }
    if (targetReply?.parentId && targetReply.parentId !== targetReply.rootId) {
      const parentReply = getEntity(targetReply.parentId);
      if (parentReply) parentReply.replyCount--;
    } else if (targetReply?.rootId) {
      const rootReply = getEntity(targetReply.rootId);
      if (rootReply) rootReply.replyCount--;
    }
    deleteItem(replyId);
  }

  async function createReply(
    params: Parameters<typeof api.createOneReply>[0],
    entityId?: number
  ) {
    // 1. 请求创建
    const res = await api.createOneReply(params);
    // 2. 更新父层count
    const { parentId } = params;
    const parentReply = getEntity(parentId);
    parentReply!.replyCount++;
    // 3. 更新reply列表
    const reply = res.item;
    updateMap([reply]);
    if (entityId) {
      entityId2ReplyId.value[entityId]!.unshift(reply.id!);
      countOwnerMap.value[entityId].replyCount++;
    } else rootId2ReplyId.value[reply.rootId]!.unshift(reply.id!);
  }

  async function updateReply(
    id: number,
    params: Parameters<typeof api.createOneReply>[0]
  ) {
    // 1. 请求创建
    const res = await api.updateOneReply(id, params);
    // 2. 更新父层count
    const { parentId } = params;
    const parentReply = getEntity(parentId);
    parentReply!.replyCount++;
    // 3. 更新reply列表
    const reply = res.item;
    updateMap([reply]);
  }

  /** Reply实体对应的ViewerCount + 1 */
  async function createOnePv(replyId: number) {
    const { data } = await api.createOnePageView({ replyId });
    updateMap([data]);
  }

  function popEmoji(target: Reply) {
    const user = useGlobalUser();
    if (!user.isLogin) {
      user.loginModal.show = true;
      return;
    }
    emojiReplyTarget.value = target;
    showEmojiPopup.value = true;
  }

  return {
    replyEntity,
    emojiList,
    emojiMap,
    replyOwnerMap,
    countOwnerMap,
    showEmojiPopup,
    emojiReplyTarget,
    voteLoading,

    vote,
    createReply,
    updateReply,
    deleteReply,
    getReply,
    getReplyCount,
    getOrFetchReplyList,
    getReplyList,
    getReplyTreeList,
    getOrFetchReplyTreeList,
    initEmojiVoteList,
    createOnePv,
    updateMap,
    popEmoji,
  };
});
